import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn, Button, ScrollArea } from '@scio3/ui';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useReferralAccess } from '@/features/referral/hooks/useReferralAccess';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { SCIO1_STATE_MAP } from '@/components/iframe/scio1-route-map';
import type { LucideIcon } from 'lucide-react';

import { PlanGate } from '@/components/shared';
import { ScrollContainer } from '@/components/shared/ScrollContainer';
import type { PlanGateProps } from '@/components/shared';
import type { getCompanyFeatures } from '@/hooks/useCompanyFeatures';
import {
  UserCircle,
  Building2,
  Users,
  Gift,
  CreditCard,
  Wallet,
  Receipt,
  Building,
  Ticket,
  ShoppingCart,
  ClipboardList,
  KeyRound,
  SlidersHorizontal,
  Calendar,
  CheckSquare,
  Database,
  Rss,
  Link,
  BarChart3,
  Bell,
  Radio,
  MapPin,
  Key,
  Plug,
  Shield,
  Fingerprint,
  ScrollText,
  FileSignature,
} from 'lucide-react';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  /** Controls sidebar visibility. Undefined = always visible. */
  show?: boolean;
  /**
   * Direct-URL access gate — checked when the page is accessed via URL.
   * - undefined: falls back to `show` (hidden from sidebar = blocked via URL)
   * - boolean: overrides `show` for access control (e.g. hidden from sidebar but accessible if permission passes)
   */
  accessGate?: boolean;
  /**
   * Renders the item with the referral "Refer & Earn" treatment (Var D): a
   * gradient gift chip in place of the plain icon + a trailing teal "$25" pill.
   * Used for the single item in the REWARDS group.
   */
  referralChip?: boolean;
  /** Trailing pill text (teal "$25" pill on the referral row). */
  pill?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Routes that render native React components (not iframe).
 *  Also used by AppShell to decide when to mount the scio1 modal adapter iframe. */
export const NATIVE_SETTINGS_SLUGS = new Set<string>();
if (import.meta.env.VITE_USE_NATIVE_PROFILE === 'true')
  NATIVE_SETTINGS_SLUGS.add('personal-profile');
if (import.meta.env.VITE_USE_NATIVE_COMPANY_PROFILE === 'true')
  NATIVE_SETTINGS_SLUGS.add('company-profile');
if (import.meta.env.VITE_USE_NATIVE_EXTERNAL_COMS === 'true')
  NATIVE_SETTINGS_SLUGS.add('external-coms');
if (import.meta.env.VITE_USE_NATIVE_PREFERENCES === 'true')
  NATIVE_SETTINGS_SLUGS.add('preference-settings');
if (import.meta.env.VITE_USE_NATIVE_APIKEYS === 'true') NATIVE_SETTINGS_SLUGS.add('apikeys');
if (import.meta.env.VITE_USE_NATIVE_FEEDS_ADMIN === 'true')
  NATIVE_SETTINGS_SLUGS.add('feeds-admin');
if (import.meta.env.VITE_USE_NATIVE_DATASOURCES === 'true')
  NATIVE_SETTINGS_SLUGS.add('datasources-admin');
if (import.meta.env.VITE_USE_NATIVE_POP_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('proof-of-play-settings');
if (import.meta.env.VITE_USE_NATIVE_INTEGRATIONS === 'true')
  NATIVE_SETTINGS_SLUGS.add('integrations');
if (import.meta.env.VITE_USE_NATIVE_BILLING === 'true') {
  NATIVE_SETTINGS_SLUGS.add('subscription-plan');
  NATIVE_SETTINGS_SLUGS.add('payment');
  NATIVE_SETTINGS_SLUGS.add('payment-history');
  // team-billing requires VITE_USE_NATIVE_TEAMS too — see below.
  NATIVE_SETTINGS_SLUGS.add('coupons');
  NATIVE_SETTINGS_SLUGS.add('payment-intent');
  NATIVE_SETTINGS_SLUGS.add('confirm-linked-account');
  NATIVE_SETTINGS_SLUGS.add('order-device');
  NATIVE_SETTINGS_SLUGS.add('ordered-device');
}
if (
  import.meta.env.VITE_USE_NATIVE_BILLING === 'true' &&
  import.meta.env.VITE_USE_NATIVE_SUB_ACCOUNT_BILLING === 'true'
)
  NATIVE_SETTINGS_SLUGS.add('sub-account-billing');
// Team Billing is gated by both flags so VITE_USE_NATIVE_BILLING=true +
// VITE_USE_NATIVE_TEAMS=false falls through to scio1 iframe at this slug
// (target state: app.account.teamBillingScio3, see SCIO1_STATE_MAP).
if (
  import.meta.env.VITE_USE_NATIVE_BILLING === 'true' &&
  import.meta.env.VITE_USE_NATIVE_TEAMS === 'true'
)
  NATIVE_SETTINGS_SLUGS.add('team-billing');
if (import.meta.env.VITE_USE_NATIVE_ACCOUNT_MEMBERS === 'true')
  NATIVE_SETTINGS_SLUGS.add('account-members');
if (import.meta.env.VITE_USE_NATIVE_SAML_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('saml-settings');
if (import.meta.env.VITE_USE_NATIVE_SHARE_LINKS_ADMIN === 'true')
  NATIVE_SETTINGS_SLUGS.add('share-links-admin');
if (import.meta.env.VITE_USE_NATIVE_OPS_SCHEDULE === 'true')
  NATIVE_SETTINGS_SLUGS.add('operational-schedule');
if (import.meta.env.VITE_USE_NATIVE_MONITORING_ALERT_RULES === 'true')
  NATIVE_SETTINGS_SLUGS.add('monitoring-alert-rules');
if (import.meta.env.VITE_USE_NATIVE_FUNCTIONAL_LOCATIONS === 'true')
  NATIVE_SETTINGS_SLUGS.add('functional-locations');
if (import.meta.env.VITE_USE_NATIVE_BRANDING_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('branding-settings');
// Referral "Refer & Earn" page — native (no scio1 equivalent) wherever the
// referral engine is wired (VITE_REFERRAL_API_URL). The enable flag
// (VITE_ENABLE_REFERRAL_ENGINE) only gates the UI entry points (sidebar item /
// pill / account-menu); the page itself stays reachable by direct URL even when
// the flag is off — for testing / soft-launch.
if (import.meta.env.VITE_REFERRAL_API_URL) NATIVE_SETTINGS_SLUGS.add('referral');
// Revue is always native — no scio1 equivalent.
NATIVE_SETTINGS_SLUGS.add('revue');

/** Company feature flags type — derived from getCompanyFeatures return. */
type CompanyFeatures = ReturnType<typeof getCompanyFeatures>;

/** PlanGateProps with planFeature narrowed to actual feature flag keys. */
type SettingsPlanGateConfig = Omit<PlanGateProps, 'planFeature'> & {
  planFeature: keyof CompanyFeatures;
};

/**
 * Settings slugs that show a native plan gate when their feature flag is disabled.
 *
 * Only includes iframe-only pages that lack a scio1 plan gate.
 * - API Keys & Data Sources: handled by their own native page components
 * - SAML & Monitoring Alerts: scio1 handles plan gating inside the iframe
 */
export const SETTINGS_PLAN_GATES: Record<string, SettingsPlanGateConfig> = {
  'approval-list': {
    planFeature: 'approvalWorkflow',
    minPlanRequired: 'proPlus',
    featureLabel: 'Approval Requests',
    planLabel: 'Pro Plus Plan',
  },
};

/** Settings slugs that render without the sidebar (full-width / standalone pages).
 *  Matches the slug exactly OR as a parent of the current path (so `order-device/:alias` also hides). */
const FULL_WIDTH_SETTINGS_SLUGS = new Set<string>([
  'payment-intent',
  'payment-thankyou',
  'order-device',
]);

/** scio1-only slugs that redirect to a scio3 equivalent (relative to /app/s/). */
const SETTINGS_REDIRECTS: Record<string, string> = {
  'android-devices': '/app/s/order-device',
  'free-devices': '/app/s/order-device',
  'subscription-thankyou': '/app/s/personal-profile',
  'subscription-init-thankyou': '/app/s/personal-profile',
  'proof-of-play-redirect': '/app/s/proof-of-play-settings',
  'request-apis': '/app/s/personal-profile',
};

function useSettingsNav(): NavGroup[] {
  const features = useCompanyFeatures();
  const { isRootAdmin, isRootSuperAdmin, isTeamAdmin, currentTeam } = usePermissions();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  // White-label sub-flags (scio1 account.html parity) — billing/subscription/PoP gate on the
  // domain `hide*` flags, not on isWhiteLabel itself.
  const hideSubscriptionPages = !!me?.brandingData?.hideSubscriptionPages;
  const hidePaymentHistoryPages = !!me?.brandingData?.hidePaymentHistoryPages;
  // OptiSigns-branded surfaces (Data Sources, Share Links, Integrations) show only when OptiSigns
  // branding is kept — hidden on white-label accounts that remove OptiSigns branding.
  const removeOptiSignsBranding = !!me?.brandingData?.removeOptiSignsBranding;
  const hideProofofPlay = !!me?.brandingData?.hideProofofPlay;
  const hideAccountMembersPage = !!me?.brandingData?.hideAccountMembersPage;
  const hideAuditLogging = !!me?.brandingData?.hideAuditLogging;
  const hideMonitorAndAlert = !!me?.brandingData?.hideMonitorAndAlert;
  const hideExternalCommunications = !!me?.brandingData?.hideExternalCommunications;
  // QC #1272 — hide Sub-Account Billing for accounts that are themselves a
  // child/linked sub-account. Backend already rejects activation with
  // "Cannot enable on child account"; hiding the tab mirrors scio1 and
  // avoids the dead-end click. parentAccountId/linkedAccountId are fetched
  // by GET_COMPANY_INFO (auth.ts) so this is populated at login time.
  const isChildAccount = !!company?.parentAccountId || !!company?.linkedAccountId;
  // scio1: isRootOwnerAndDefaultTeam() = isRootAdmin && (teamId == '1' || !teamId)
  const { isDefaultTeam } = useTeamStore();
  const isRootAdminDefaultTeam = isRootAdmin && isDefaultTeam;
  const canApprove = isTeamAdmin || !!currentTeam?.permissions?.playlistCanApprove;
  const { entryEnabled, pageAccessible, loading: referralLoading } = useReferralAccess();

  return [
    {
      label: 'Account',
      items: [
        { icon: UserCircle, label: 'Personal Profile', path: '/app/s/personal-profile' },
        {
          icon: Building2,
          label: 'Company Settings',
          path: '/app/s/company-profile',
          show: isRootAdmin && !isWhiteLabel, // scio1: hidden for white-label
        },
        {
          icon: Users,
          label: 'Account Members',
          path: '/app/s/account-members',
          show: !hideAccountMembersPage, // scio1: gated on hideAccountMembersPage
        },
      ],
    },
    {
      // "Refer & Earn" lives in its own REWARDS section (right after ACCOUNT)
      // rather than buried under Billing & Orders — it's a growth surface, not a
      // billing page. The row gets the gradient gift chip + teal "$25" pill
      // treatment (Var D) so it matches the account-dropdown promo card.
      label: 'Rewards',
      items: [
        {
          icon: Gift,
          label: 'Refer & Earn',
          path: '/app/s/referral',
          show: entryEnabled, // sidebar entry only when the flag is on
          // Defer the real subscription gate to ReferralPage while company data is still
          // loading — there's a transient where me is truthy but company is null, which
          // makes pageAccessible false even for paid subscribers. Treating accessGate as
          // true during loading prevents a premature redirect before company resolves.
          accessGate: referralLoading ? true : pageAccessible,
          referralChip: true,
          pill: 'Get $25',
        },
      ],
    },
    {
      label: 'Billing & Orders',
      items: [
        {
          icon: CreditCard,
          label: 'Subscription Plan',
          path: '/app/s/subscription-plan',
          show: isRootAdmin && !hideSubscriptionPages, // scio1: gated on hideSubscriptionPages
        },
        {
          icon: Wallet,
          label: 'Payment Settings',
          path: '/app/s/payment',
          show: isRootAdmin && !hideSubscriptionPages,
        },
        {
          icon: Receipt,
          label: 'Billing History',
          path: '/app/s/payment-history',
          show: isRootAdmin && !hidePaymentHistoryPages, // scio1: gated on hidePaymentHistoryPages
        },
        {
          icon: Building,
          label: 'Sub-Account Billing',
          path: '/app/s/sub-account-billing',
          show: isRootAdmin && !isChildAccount && !isWhiteLabel, // scio1: hidden for white-label
          accessGate: isRootAdmin && !isChildAccount && !isWhiteLabel,
        },
        {
          icon: Building,
          label: 'Team Billing',
          path: '/app/s/team-billing',
          // Sidebar entry intentionally hidden — the page is deep-link-only,
          // reached via the per-team "Team Billing" button on Account Members
          // (`/app/s/team-billing?teamId=<id>`). The old list view was a temp
          // test surface and is no longer exposed.
          show: false,
          // Direct URL access still allowed for Root Super Admin OR any team
          // admin/owner. Per-team data access is enforced inside the page via
          // backend `getTeamBillingInfo` (filters by team membership).
          accessGate: (isRootSuperAdmin || isTeamAdmin) && features.teamBilling,
        },
        {
          icon: Ticket,
          label: 'Coupons',
          path: '/app/s/coupons',
          // scio1 parity: topNavigation.html:178-181 — `isRootOwnerAndDefaultTeam() && !isWhiteLabel`.
          // Despite v1's misleading name, principal.js:64-67 evaluates `isRootAdmin + default team`.
          show: isRootAdminDefaultTeam && !isWhiteLabel,
          accessGate: isRootAdminDefaultTeam && !isWhiteLabel,
        },
        {
          icon: ShoppingCart,
          label: 'Order Devices',
          path: '/app/s/order-device',
          show: isRootAdminDefaultTeam && !isWhiteLabel,
        },
        {
          icon: ClipboardList,
          label: 'Order History',
          path: '/app/s/ordered-device',
          show: isRootAdminDefaultTeam && !isWhiteLabel,
        },
      ],
    },
    {
      label: 'Settings',
      items: [
        {
          icon: KeyRound,
          label: 'Credentials',
          path: '/app/s/credentials',
          // Hidden from sidebar — but accessible via direct URL if user has permission
          show: false,
          accessGate: isRootAdmin && (features.apiKey || features.apiAccessToken),
        },
        {
          icon: SlidersHorizontal,
          label: 'Preferences',
          path: '/app/s/preference-settings',
          show: isRootSuperAdmin && !isWhiteLabel,
        },
        {
          icon: Calendar,
          label: 'Operational Schedule',
          path: '/app/s/operational-schedule',
          show: isRootAdmin && !isWhiteLabel, // scio1: hidden for white-label
        },
        {
          icon: CheckSquare,
          label: 'Approval Requests',
          path: '/app/s/approval-list',
          // Hidden from sidebar — but accessible via direct URL if user has permission
          show: false,
          accessGate: canApprove,
        },
      ],
    },
    {
      label: 'Data & Monitoring',
      items: [
        {
          icon: Database,
          label: 'Data Sources',
          path: '/app/s/datasources-admin',
          show: isTeamAdmin && !removeOptiSignsBranding,
        },

        {
          icon: Rss,
          label: 'Feeds',
          path: '/app/s/feeds-admin',
          show: isRootAdmin && features.feedsCreate,
        },
        {
          icon: Link,
          label: 'Share Links',
          path: '/app/s/share-links-admin',
          show: isTeamAdmin && features.shareLink && !removeOptiSignsBranding,
        },
        {
          icon: BarChart3,
          label: 'Proof of Play',
          path: '/app/s/proof-of-play-settings',
          show: isRootAdmin && !hideProofofPlay, // scio1: gated on hideProofofPlay
        },
        {
          icon: Bell,
          label: 'Monitoring Alerts',
          path: '/app/s/monitoring-alert-rules',
          // scio1: hidden by hideMonitorAndAlert; on white-label also needs the alert-policy add-on.
          show: isRootAdmin && !hideMonitorAndAlert && (!isWhiteLabel || features.alertPolicy),
        },
        {
          icon: ScrollText,
          label: 'Audit Log',
          path: '/app/security-audit-logging',
          // scio1: hidden by hideAuditLogging; on white-label also needs the audit-logging add-on.
          show:
            isRootAdmin && !hideAuditLogging && (!isWhiteLabel || features.securityAuditLogging),
        },
      ],
    },
    {
      label: 'Advanced',
      items: [
        {
          icon: Shield,
          label: 'Branding Settings',
          path: '/app/s/branding-settings',
          // scio1: hidden for white-label (removeOptiSignsBranding || isWhiteLabel). Page still
          // reachable by direct URL. Consistent with the profile dropdown (UserMenu).
          show: isRootAdmin && !isWhiteLabel,
        },
        {
          icon: Fingerprint,
          label: 'SAML Single Sign On',
          path: '/app/s/saml-settings',
          show: isRootAdmin && !isWhiteLabel,
        },
        {
          icon: Radio,
          label: 'External Communications',
          path: '/app/s/external-coms',
          // main base (isTeamAdmin) + white-label gate from this branch: hidden by
          // hideExternalCommunications; on white-label also needs the external-com add-on.
          show:
            isTeamAdmin && !hideExternalCommunications && (!isWhiteLabel || features.externalCom),
        },
        {
          icon: MapPin,
          label: 'Functional Locations',
          path: '/app/s/functional-locations',
          // scio1 parity: host-scoped `account.domain.removeOptiSignsBranding` is not equivalent to scio3's account-scoped `me.brandingData`, so we gate on isWhiteLabel only.
          show: isRootAdmin && !isWhiteLabel,
        },
        {
          icon: Key,
          label: 'API Keys',
          path: '/app/s/apikeys',
          show: isRootAdmin && !isWhiteLabel,
        },
        {
          icon: Plug,
          label: 'Integrations',
          path: '/app/s/integrations',
          show: isTeamAdmin && !removeOptiSignsBranding,
        },
        {
          icon: FileSignature,
          label: 'Revue Workflow',
          path: '/app/s/revue',
          show: isRootAdmin && features.contentApproval,
        },
      ],
    },
  ];
}

export function SettingsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navGroups = useSettingsNav();
  const features = useCompanyFeatures();
  const me = useAuthStore((s) => s.me);

  // Determine if current route is native or iframe
  const slug = location.pathname.replace('/app/s/', '');
  // Match both exact slug and sub-paths (e.g. "order-device/android-stick" matches "order-device")
  const isNativePage =
    NATIVE_SETTINGS_SLUGS.has(slug) ||
    (() => {
      for (const s of NATIVE_SETTINGS_SLUGS) {
        if (slug.startsWith(s + '/')) return true;
      }
      return false;
    })();

  // Access check: redirect users who don't have permission to view this settings page.
  // Build a map of path → NavItem for quick lookup.
  const currentNavItem = useMemo(() => {
    for (const group of navGroups) {
      for (const item of group.items) {
        if (item.path === location.pathname) return item;
      }
    }
    return null;
  }, [navGroups, location.pathname]);

  // Redirect if the nav item exists but the user lacks permission.
  // accessGate (when defined) overrides show for direct-URL access control.
  const isAccessDenied =
    !!me &&
    !!currentNavItem &&
    (() => {
      if (currentNavItem.accessGate !== undefined) return !currentNavItem.accessGate;
      return currentNavItem.show === false;
    })();

  useEffect(() => {
    if (isAccessDenied) {
      toast.error(t('You do not have permission to access this page.'), {
        id: 'settings-access-denied',
      });
      navigate('/app/screenManagement', { replace: true });
    }
  }, [isAccessDenied, navigate, t]);

  // Plan gate: show upgrade screen instead of iframe when feature flag is off
  const planGateConfig = SETTINGS_PLAN_GATES[slug] ?? null;
  const isPlanGated = planGateConfig !== null && !features[planGateConfig.planFeature];

  // Resolve target scio1 state for the iframe
  const routeKey = location.pathname.replace('/app/', '');
  const targetState = SCIO1_STATE_MAP[routeKey];

  // Redirect scio1-only slugs that have no scio3 equivalent
  useEffect(() => {
    const target = SETTINGS_REDIRECTS[slug];
    if (target) navigate(target, { replace: true });
  }, [slug, navigate]);

  // Hide sidebar for pages that should be full-width (e.g. order-device is a standalone shop flow).
  // Matches the slug exactly or as a parent prefix so `order-device/<alias>` also hides.
  const hideSidebar =
    FULL_WIDTH_SETTINGS_SLUGS.has(slug) ||
    (() => {
      for (const s of FULL_WIDTH_SETTINGS_SLUGS) {
        if (slug.startsWith(s + '/')) return true;
      }
      return false;
    })();

  // Don't render anything while redirecting due to access denial
  if (isAccessDenied) return null;

  return (
    <div data-settings className="flex h-full bg-muted">
      <aside className={cn('hidden w-60 shrink-0 lg:block', hideSidebar && 'lg:hidden')}>
        <ScrollArea className="h-full py-3">
          <nav className="space-y-4 px-2">
            {navGroups.map((group) => {
              const visibleItems = group.items.filter((item) => item.show !== false);
              if (visibleItems.length === 0) return null;

              return (
                <div key={group.label}>
                  <h3 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {t(group.label)}
                  </h3>
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const isActive =
                        location.pathname === item.path ||
                        location.pathname.startsWith(item.path + '/');
                      const Icon = item.icon;

                      // Slug-keyed testid for E2E targeting (e.g. settings-nav-provisioning-templates).
                      // Stable across i18n locale + label changes; safer than text-based locators.
                      // Use last URL segment so paths outside /app/s/ (e.g. Audit Log at
                      // /app/security-audit-logging) still produce a clean slug.
                      const slug = item.path.split('/').filter(Boolean).pop() ?? item.path;
                      return (
                        <Button
                          key={item.path}
                          variant="ghost"
                          size="sm"
                          data-testid={`settings-nav-${slug}`}
                          className={cn(
                            'w-full justify-start gap-2.5 rounded-md px-3 text-[13px] font-normal',
                            isActive
                              ? 'bg-sidebar-active font-medium text-foreground hover:bg-sidebar-active'
                              : 'text-muted-foreground hover:bg-sidebar-active/60 hover:text-foreground',
                          )}
                          onClick={() => navigate(item.path)}
                        >
                          {item.referralChip ? (
                            // Var D: gradient gift chip (~24px rounded square,
                            // white gift) instead of the plain monochrome icon.
                            // The gradient lives in `.ref-gift-chip` (referral
                            // overrides CSS) so it stays in sync with the
                            // dropdown card + widget hero.
                            <span className="ref-gift-chip flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] text-white shadow-[0_6px_12px_-6px_rgba(0,208,110,0.65)]">
                              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                            </span>
                          ) : (
                            <Icon
                              className={cn(
                                'h-4 w-4 shrink-0',
                                isActive ? 'text-primary' : 'text-muted-foreground/70',
                              )}
                            />
                          )}
                          <span className={cn(item.referralChip && 'text-foreground')}>
                            {t(item.label)}
                          </span>
                          {item.pill && (
                            <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10.5px] font-bold text-secondary-foreground">
                              {item.pill}
                            </span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </ScrollArea>
      </aside>
      <main className="flex min-h-0 flex-1 px-2 pb-2">
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-card">
          {/* Plan gate overlay — shown on top of iframe when feature flag is off */}
          {isPlanGated && planGateConfig && <PlanGate {...planGateConfig} />}
          {/* Native page content rendered via Outlet */}
          {isNativePage && !isPlanGated && (
            <ScrollContainer className="h-full">
              <Outlet />
            </ScrollContainer>
          )}
          {/* Persistent iframe — h-full fills the flex-1 wrapper height */}
          <div
            className={cn(
              'min-h-0 flex-1 overflow-hidden',
              (isNativePage || isPlanGated) && 'hidden',
            )}
          >
            <Scio1Frame targetState={targetState} className="h-full w-full border-0" />
          </div>
        </div>
      </main>
    </div>
  );
}
